import type { DomainCode } from "@/constants/domains";
import { getDomainByCode } from "@/constants/domains";
import type { Assessment, AssessmentEntry, ScoreSummary } from "@/types/assessment";
import type { ScaleEntry, ScaleLibrary } from "@/types/scale";
import { assessmentRecordLabel } from "./student-workspace";

export interface ReportDomainStat {
  code: DomainCode;
  name: string;
  totalEntries: number;
  completed: number;
  score: number;
  maxScore: number;
  average: number | null;
  scorePercent: number;
  completionPercent: number;
}

export interface ReportSubdomainStat {
  key: string;
  domainCode: DomainCode;
  label: string;
  name: string;
  totalEntries: number;
  completed: number;
  score: number;
  maxScore: number;
  average: number | null;
  scorePercent: number;
  completionPercent: number;
}

export interface AssessmentAnalysisPoint {
  assessment: Assessment;
  entries: AssessmentEntry[];
  observations: Array<{ entryCode: string; comment: string }>;
  domainStats: ReportDomainStat[];
  subdomainStats: ReportSubdomainStat[];
  totalScore: number;
  totalMaxScore: number;
  totalAverage: number | null;
  totalCompleted: number;
  totalCatalogEntries: number;
  totalScorePercent: number;
  totalCompletionPercent: number;
  hasScores: boolean;
}

export interface AnalyticsPresentation {
  showIntervalChange: boolean;
  showTotalTrend: boolean;
  totalGrowth: number | null;
  averageGrowth: number | null;
  domainAverageSeries: Array<{ name: string; value: number }>;
}

export function buildAnalyticsPresentation(
  points: AssessmentAnalysisPoint[],
): AnalyticsPresentation {
  const latest = points[points.length - 1];
  const first = points[0];
  const hasInterval = points.length >= 2 && Boolean(first && latest);
  const totalGrowth = hasInterval ? latest.totalScore - first.totalScore : null;
  return {
    showIntervalChange: hasInterval,
    showTotalTrend: hasInterval,
    totalGrowth,
    averageGrowth:
      totalGrowth === null ? null : totalGrowth / Math.max(points.length - 1, 1),
    domainAverageSeries: (latest?.domainStats ?? []).map((domain) => ({
      name: domain.name.replace("领域", ""),
      value: Number((domain.average ?? 0).toFixed(2))
    }))
  };
}

interface MutableStat {
  completed: number;
  score: number;
}

export function buildAssessmentAnalysisPoint(
  assessment: Assessment,
  entries: AssessmentEntry[],
  scaleLibrary: ScaleLibrary,
): AssessmentAnalysisPoint {
  const domainCatalog = buildDomainCatalog(scaleLibrary);
  const subdomainCatalog = buildSubdomainCatalog(scaleLibrary);
  const scoredEntries = entries.filter(
    (entry) => entry.score !== null && scaleLibrary.entryIndex.has(entry.entryCode),
  );

  const domainSums = new Map<DomainCode, MutableStat>();
  const subdomainSums = new Map<string, MutableStat>();

  if (scoredEntries.length > 0) {
    scoredEntries.forEach((record) => {
      const entry = scaleLibrary.entryIndex.get(record.entryCode);
      if (!entry || record.score === null) {
        return;
      }
      addScore(domainSums, entry.domainCode, record.score);
      addScore(subdomainSums, subdomainKey(entry), record.score);
    });
  } else if (assessment.scoreSummary) {
    addScoreSummaryFallback(assessment.scoreSummary, domainSums, subdomainSums);
  }

  const domainStats = domainCatalog.map((domain) => {
    const stat = domainSums.get(domain.code) ?? { completed: 0, score: 0 };
    return buildDomainStat(domain.code, domain.totalEntries, stat);
  });

  const subdomainStats = subdomainCatalog.map((subdomain) => {
    const stat = subdomainSums.get(subdomain.key) ?? { completed: 0, score: 0 };
    const maxScore = stat.completed * 2;
    return {
      ...subdomain,
      completed: stat.completed,
      score: stat.score,
      maxScore,
      average: stat.completed > 0 ? stat.score / stat.completed : null,
      scorePercent: maxScore > 0 ? stat.score / maxScore : 0,
      completionPercent:
        subdomain.totalEntries > 0 ? stat.completed / subdomain.totalEntries : 0
    };
  });

  const totalScore = domainStats.reduce((sum, domain) => sum + domain.score, 0);
  const totalCompleted = domainStats.reduce((sum, domain) => sum + domain.completed, 0);
  const totalCatalogEntries = domainStats.reduce((sum, domain) => sum + domain.totalEntries, 0);
  const totalMaxScore = totalCompleted * 2;

  return {
    assessment,
    entries,
    observations: entries
      .filter((entry) => entry.comment?.trim())
      .map((entry) => ({ entryCode: entry.entryCode, comment: entry.comment!.trim() })),
    domainStats,
    subdomainStats,
    totalScore,
    totalMaxScore,
    totalAverage: totalCompleted > 0 ? totalScore / totalCompleted : null,
    totalCompleted,
    totalCatalogEntries,
    totalScorePercent: totalMaxScore > 0 ? totalScore / totalMaxScore : 0,
    totalCompletionPercent:
      totalCatalogEntries > 0 ? totalCompleted / totalCatalogEntries : 0,
    hasScores: totalCompleted > 0
  };
}

export function sortAnalysisPoints(
  points: AssessmentAnalysisPoint[],
): AssessmentAnalysisPoint[] {
  return [...points].sort(
    (a, b) =>
      new Date(a.assessment.startedAt).getTime() -
      new Date(b.assessment.startedAt).getTime(),
  );
}

export function formatScore(value: number, digits = 1): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(digits);
}

export function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function renderAssessmentType(_type: Assessment["type"]): string {
  void _type;
  return assessmentRecordLabel();
}

export function renderAssessmentStatus(status: Assessment["status"]): string {
  return status === "completed" ? "已完成" : "进行中";
}

export function areAssessmentsComparable(
  reference: Assessment,
  candidate: Assessment,
): boolean {
  return (
    candidate.studentId === reference.studentId &&
    candidate.sourceVersion === reference.sourceVersion &&
    candidate.coverageSignature === reference.coverageSignature
  );
}

export function resolveComparableAssessmentIds(
  requestedIds: string[],
  assessments: Assessment[],
  limit = Number.POSITIVE_INFINITY,
): string[] {
  const assessmentById = new Map(
    assessments.map((assessment) => [assessment.id, assessment]),
  );
  const requestedAssessments = requestedIds
    .map((id) => assessmentById.get(id))
    .filter((assessment): assessment is Assessment => assessment !== undefined);
  const reference = requestedAssessments[0];
  if (!reference) {
    return [];
  }
  return requestedAssessments
    .filter((assessment) => areAssessmentsComparable(reference, assessment))
    .slice(0, limit)
    .map((assessment) => assessment.id);
}

function buildDomainCatalog(scaleLibrary: ScaleLibrary): Array<{
  code: DomainCode;
  totalEntries: number;
}> {
  return scaleLibrary.domains.map((domain) => ({
    code: domain.domainCode,
    totalEntries: scaleLibrary.entries.filter(
      (entry) => entry.domainCode === domain.domainCode,
    ).length
  }));
}

function buildSubdomainCatalog(scaleLibrary: ScaleLibrary): Array<{
  key: string;
  domainCode: DomainCode;
  label: string;
  name: string;
  totalEntries: number;
}> {
  return scaleLibrary.domains.flatMap((domain) =>
    domain.subdomains.map((subdomain) => ({
      key: `${domain.domainCode}-${subdomain.subdomainCode}`,
      domainCode: domain.domainCode,
      label: subdomain.subdomainLabel,
      name: subdomain.subdomainName,
      totalEntries: scaleLibrary.entries.filter(
        (entry) =>
          entry.domainCode === domain.domainCode &&
          entry.subdomainCode === subdomain.subdomainCode,
      ).length
    })),
  );
}

function buildDomainStat(
  code: DomainCode,
  totalEntries: number,
  stat: MutableStat,
): ReportDomainStat {
  const maxScore = stat.completed * 2;
  return {
    code,
    name: getDomainByCode(code).name,
    totalEntries,
    completed: stat.completed,
    score: stat.score,
    maxScore,
    average: stat.completed > 0 ? stat.score / stat.completed : null,
    scorePercent: maxScore > 0 ? stat.score / maxScore : 0,
    completionPercent: totalEntries > 0 ? stat.completed / totalEntries : 0
  };
}

function addScore<T>(map: Map<T, MutableStat>, key: T, score: number): void {
  const stat = map.get(key) ?? { completed: 0, score: 0 };
  stat.completed += 1;
  stat.score += score;
  map.set(key, stat);
}

function addScoreSummaryFallback(
  summary: ScoreSummary,
  domainSums: Map<DomainCode, MutableStat>,
  subdomainSums: Map<string, MutableStat>,
): void {
  const domainScores = isRecord(summary.domainScores) ? summary.domainScores : {};
  const subdomainScores = isRecord(summary.subdomainScores) ? summary.subdomainScores : {};

  Object.entries(domainScores).forEach(([code, value]) => {
    if (!isRecord(value)) {
      return;
    }
    const completed = readNumber(value.completed);
    const average = readNullableNumber(value.average);
    domainSums.set(code as DomainCode, {
      completed,
      score: average !== null ? average * completed : 0
    });
  });

  Object.entries(subdomainScores).forEach(([key, value]) => {
    if (!isRecord(value)) {
      return;
    }
    const completed = readNumber(value.completed);
    const average = readNullableNumber(value.average);
    subdomainSums.set(key, {
      completed,
      score: average !== null ? average * completed : 0
    });
  });
}

function subdomainKey(entry: ScaleEntry): string {
  return `${entry.domainCode}-${entry.subdomainCode}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function readNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
